import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import GoogleSignInButton from "../components/GoogleSignInButton";

const REF_KEY = "dealhub.ref";
const REF_DEAL_KEY = "dealhub.ref_deal";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [refInfo, setRefInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const id = localStorage.getItem(REF_KEY);
      const deal = localStorage.getItem(REF_DEAL_KEY);
      if (id) setRefInfo({ id, deal });
    } catch (e) { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const extra = refInfo ? { referrer_id: refInfo.id, referrer_deal_id: refInfo.deal } : {};
      const data = await register(name, email, password, extra);
      try { localStorage.removeItem(REF_KEY); localStorage.removeItem(REF_DEAL_KEY); } catch (e) { /* ignore */ }
      toast.success(data?.referred ? "Welcome — your friend just earned Pro credit 🎁" : "Welcome to DealHub");
      navigate("/preferences?welcome=1");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="register-page">
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Join</div>
      <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter mb-8">Create account</h1>
      {refInfo && (
        <div className="mb-6 border-2 border-[#E63946] bg-[#FFF5F6] p-4" data-testid="register-referral-banner">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-1">🎁 You were invited</div>
          <p className="text-sm font-serif text-black/80">
            Sign up and your friend gets <span className="font-bold">30 days of DealHub Pro</span> — on the house.
          </p>
        </div>
      )}
      <GoogleSignInButton label="Sign up with Google" testId="google-signin-register" />
      <div className="flex items-center gap-3 my-6" aria-hidden="true">
        <div className="flex-1 h-px bg-black/10" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-black/40">or with email</span>
        <div className="flex-1 h-px bg-black/10" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-2">Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-black px-4 py-3 focus:outline-none focus:bg-black focus:text-white" data-testid="register-name" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-2">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-black px-4 py-3 focus:outline-none focus:bg-black focus:text-white" data-testid="register-email" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-2">Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-black px-4 py-3 focus:outline-none focus:bg-black focus:text-white" data-testid="register-password" />
        </div>
        <button type="submit" disabled={loading} className="w-full bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50" data-testid="register-submit">
          {loading ? "…" : "Create account"}
        </button>
      </form>
      <p className="text-sm text-black/60 mt-6">
        Already have an account?{" "}
        <Link to="/login" className="underline hover:text-[#E63946]" data-testid="to-login">Log in</Link>
      </p>
    </div>
  );
}
